import { FC, useEffect, useRef, useCallback, useState, useMemo } from "react";
import {
  useField,
  UseFieldHookProps,
  transformDependentFieldsState,
  AlwaysRunPostValidationSetCrossFieldValuesType,
} from "packages/form";
import { TextFieldProps } from "@mui/material/TextField";
import { TextField } from "components/styledComponent";
import InputAdornment from "@mui/material/InputAdornment";
import Grid, { GridProps } from "@mui/material/Grid";
import CircularProgress, {
  CircularProgressProps,
} from "@mui/material/CircularProgress";
import { Merge } from "../types";
import { numWords } from "components/common/utils";
import FormHelperText from "@mui/material/FormHelperText";
import { t } from "i18next";
import * as Icons from "@mui/icons-material";
import { SxProps } from "@mui/material";
import { useAuthContext } from "components/context/authContext/authContext";

interface MyGridExtendedProps {
  enableNumWords?: boolean;
  maxLength?: number;
  GridProps?: GridProps;
  StartAdornment?: string;
  EndAdornment?: string;
  CircularProgressProps?: CircularProgressProps;
  enableGrid: boolean;
  setValueOnDependentFieldsChange?: any;
  setColor?: any;
  showMaxLength?: boolean;
  allowToggleVisiblity?: boolean;
  startsIcon?: keyof typeof Icons;
  endsIcon?: keyof typeof Icons;
  iconStyle?: any;
  textFieldStyle?: SxProps;
  txtTransform?: "uppercase" | "lowercase";
  AlwaysRunPostValidationSetCrossFieldValues?:
  | AlwaysRunPostValidationSetCrossFieldValuesType
  | ((
    dependentFields: any,
  ) => AlwaysRunPostValidationSetCrossFieldValuesType);
  ignoreInSubmit?: (
    dependentValues: any,
    object: { isSubmitting?: boolean; value?: string },
  ) => boolean | boolean;
  // ignoreInSubmit?: boolean;
  setFieldLabel?: (
    dependentFields?: any,
    value?: any,
  ) => { label: string; placeholder?: string } | null | undefined;
  label?: string;
  preventSpecialChars?: string | null;
  handleKeyDown?: (
    event: any,
    dependentValues: any,
    authState: any,
    formState: any,
  ) => void;
}

type MyTextFieldAllProps = Merge<TextFieldProps, MyGridExtendedProps>;

export type MyTextFieldProps = UseFieldHookProps & MyTextFieldAllProps;

const MyTextField: FC<MyTextFieldProps> = ({
  name: fieldName,
  validate,
  validationRun,
  postValidationSetCrossFieldValues,
  runValidationOnDependentFieldsChange,
  runPostValidationHookAlways,
  shouldExclude,
  isReadOnly,
  dependentFields,
  fieldKey: fieldID,
  GridProps,
  CircularProgressProps,
  enableGrid,
  enableNumWords,
  InputProps,
  inputProps,
  StartAdornment,
  EndAdornment,
  //@ts-ignore
  isFieldFocused,
  maxLength = -1,
  setValueOnDependentFieldsChange,
  skipValueUpdateFromCrossFieldWhenReadOnly,
  setColor,
  showMaxLength = true,
  startsIcon,
  endsIcon,
  iconStyle,
  textFieldStyle,
  txtTransform,
  AlwaysRunPostValidationSetCrossFieldValues,
  ignoreInSubmit = false,
  label,
  setFieldLabel,
  preventSpecialChars,
  placeholder,
  handleKeyDown,
  runExternalFunction,
  onFormDataChange,
  ...others
}) => {
  // let StartIcon = startsIcon ? Icons[startsIcon] : null;
  // let EndIcon = endsIcon ? Icons[endsIcon] : null;
  const { authState } = useAuthContext();
  const {
    value,
    error,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    validationRunning,
    fieldKey,
    name,
    excluded,
    readOnly,
    incomingMessage,
    whenToRunValidation,
    runValidation,
    validationAPIResult,
    dependentValues,
    setErrorAsCB,
    setIgnoreInSubmit,
    formState,
  } = useField({
    name: fieldName,
    fieldKey: fieldID,
    dependentFields,
    validate,
    validationRun,
    runPostValidationHookAlways,
    postValidationSetCrossFieldValues,
    isReadOnly,
    shouldExclude,
    runValidationOnDependentFieldsChange,
    skipValueUpdateFromCrossFieldWhenReadOnly,
    txtTransform,
    AlwaysRunPostValidationSetCrossFieldValues,
    runExternalFunction,
    onFormDataChange,
  });

  const [currentColor, setCurrentColor] = useState<string>(
    typeof setColor === "string" ? setColor : "",
  );
  const focusRef = useRef();
  const inputfocusRef = useRef<any>();

  useEffect(() => {
    if (typeof setColor === "function") {
      let result = setColor(value);
      if (typeof result !== "string") {
        setCurrentColor("");
      } else {
        setCurrentColor(result);
      }
    }
  }, [value, setColor]);

  const customHandleChange = useCallback(
    (e) => {
      if (
        preventSpecialChars &&
        e.target.value &&
        preventSpecialChars
          .split("")
          .some((char) => e.target.value.includes(char))
      ) {
        return;
      }
      handleChange(e, e.target?.formattedValue ?? undefined);
    },
    [handleChange],
  );

  useEffect(() => {
    if (isFieldFocused) {
      getFocus(); // cbs update
    }
  }, [isFieldFocused, value]);

  // cbs update
  const getFocus = () => {
    setTimeout(() => {
      //@ts-ignore
      focusRef?.current?.focus?.();
      ////////////Below solution is temporary for set focus in numberFormat Component.
      ////////////Need to find proper solution. If you found proper solution contact Me😎🤷‍♂️.
      if (!Boolean(focusRef?.current)) {
        inputfocusRef?.current?.children?.[1]?.lastChild?.focus?.();
      }
    }, 50);
  };

  // updated as per cbs for setValueOnDependentFieldsChange
  useEffect(() => {
    const handleDependentFieldsChange = async () => {
      if (typeof setValueOnDependentFieldsChange === "function") {
        try {
          let result = await setValueOnDependentFieldsChange(
            transformDependentFieldsState(dependentValues),
            { value: value, isSubmitting: isSubmitting },
          );

          if (result !== undefined && result !== null) {
            handleChange(result);
          }
        } catch (error) {
          // Handle any errors that occur during the promise execution
          console.error("An error occurred:", error);
        }
      }
    };
    handleDependentFieldsChange();
  }, [dependentValues, handleChange, setValueOnDependentFieldsChange]);

  // cbs update for incommingMessage properties
  useEffect(() => {
    if (incomingMessage !== null && typeof incomingMessage === "object") {
      const { value, error, ignoreUpdate, isFieldFocused, isErrorBlank } =
        incomingMessage;
      if (Boolean(value) || value === "") {
        handleChange(value, undefined, true);
        if (isFieldFocused) {
          getFocus();
        }
        if (!ignoreUpdate && whenToRunValidation === "onBlur") {
          runValidation({ value: value }, true);
        }
      }
      if (isErrorBlank) {
        setErrorAsCB("");
      } else if (Boolean(error)) {
        setErrorAsCB(error);
      }
    }
  }, [
    incomingMessage,
    handleChange,
    runValidation,
    whenToRunValidation,
    setErrorAsCB,
  ]);

  useEffect(() => {
    if (Boolean(ignoreInSubmit)) {
      if (typeof ignoreInSubmit === "function") {
        let result = ignoreInSubmit(
          transformDependentFieldsState(dependentValues),
          { isSubmitting, value },
        );
        setIgnoreInSubmit(result);
      } else {
        setIgnoreInSubmit(ignoreInSubmit);
      }
    }
  }, [ignoreInSubmit, setIgnoreInSubmit, dependentValues]);

  // useEffect(() => {
  //   setIgnoreInSubmit(ignoreInSubmit);
  // }, [ignoreInSubmit]);

  const updatedLabel = useMemo(() => {
    if (typeof setFieldLabel === "function")
      return setFieldLabel(
        transformDependentFieldsState(dependentValues),
        value,
      );
  }, [setFieldLabel, dependentValues, value]);

  if (excluded) {
    return null;
  }
  let myError = error;
  let numWordsVar: any = null;
  let myTouch = touched;
  try {
    if (enableNumWords && Boolean(value)) {
      let amountArray = String(value).split(".");
      let amountPart = Number(amountArray[0]);
      if (amountPart < 0) {
        amountPart = Math.abs(Number(amountPart));
        numWordsVar = `Negative ${numWords(amountPart)} Rupees`;
      } else {
        numWordsVar = `${numWords(amountPart)} Rupees`;
      }
      if (amountArray.length === 2 && Boolean(amountArray[1])) {
        numWordsVar = `${numWordsVar} and ${numWords(amountArray[1])} paise`;
      }
    }
  } catch (e) {
    myError = "";
    myTouch = true;
  }
  /*fix for numberFormat*/
  if (InputProps?.inputProps) {
    InputProps.inputProps = {
      ...InputProps.inputProps,
      readOnly: readOnly,
      tabIndex: readOnly ? -1 : undefined,
    };
  }
  const isError = myTouch && Boolean(myError);
  const result = (
    <TextField
      {...others}
      key={fieldKey}
      id={fieldKey}
      label={updatedLabel?.label ? t(updatedLabel.label) : label}
      placeholder={
        updatedLabel?.placeholder ? t(updatedLabel.placeholder) : placeholder
      }
      name={name}
      value={typeof value === "string" ? value.trimStart() : value}
      error={!isSubmitting && isError}
      helperText={
        <div style={{ display: "flex" }}>
          <FormHelperText>
            {!isSubmitting && isError
              ? t(myError)
              : Boolean(validationAPIResult)
                ? validationAPIResult
                : numWordsVar}
          </FormHelperText>
          {maxLength > 0 && Boolean(showMaxLength) ? (
            <FormHelperText
              error={false}
              disabled={isSubmitting}
              style={{
                flex: 1,
                textAlign: "right",
                margin: "5px 15px 0 0",
              }}
            >
              {value.length}/{maxLength}
            </FormHelperText>
          ) : null}
        </div>
      }
      FormHelperTextProps={{
        //@ts-ignore
        component: "div",
      }}
      sx={{
        "& .MuiInputBase-root": {
          background: Boolean(readOnly) ? "#f3f3f3 !important" : "",
        },
        ...textFieldStyle,
      }}
      // InputProps cbs update
      InputProps={{
        style: {
          // background: textFieldStyle ? "" : Boolean(readOnly) ? "#f3f3f3" : "",
          ...(!isSubmitting && Boolean(currentColor)
            ? {
              color: currentColor,
              fontWeight: "bold",
            }
            : {}),
        },
        endAdornment: validationRunning ? (
          <InputAdornment position="end">
            <CircularProgress
              color="secondary"
              variant="indeterminate"
              size={20}
              style={{ marginRight: "8px" }}
              {...CircularProgressProps}
            />
          </InputAdornment>
        ) : Boolean(EndAdornment) ? (
          <InputAdornment position="end" className="withBorder">
            {EndAdornment}
          </InputAdornment>
        ) : null,
        startAdornment:
          StartAdornment === "password-loader" && validationRunning ? (
            <InputAdornment
              position="end"
              sx={{ position: "absolute", right: "38px" }}
            >
              <CircularProgress
                color="secondary"
                variant="indeterminate"
                size={20}
                {...CircularProgressProps}
              />
            </InputAdornment>
          ) : Boolean(StartAdornment) &&
            StartAdornment !== "password-loader" ? (
            <InputAdornment position="start">{StartAdornment}</InputAdornment>
          ) : null,
        ...InputProps,
      }}
      InputLabelProps={{
        shrink: true,
      }}
      inputRef={focusRef}
      onChange={(e) => {
        if (maxLength === -1) {
          customHandleChange(e);
        } else if (e.target.value.length <= maxLength) {
          customHandleChange(e);
        }
      }}
      inputProps={{
        readOnly: readOnly,
        tabIndex: readOnly ? -1 : undefined,
        ...inputProps,
      }}
      onBlur={handleBlur}
      onKeyDown={(e) =>
        typeof handleKeyDown === "function"
          ? handleKeyDown(
            e,
            transformDependentFieldsState(dependentValues),
            authState,
            formState,
          )
          : undefined
      }
      disabled={isSubmitting}
      ref={inputfocusRef}
    />
  );
  if (Boolean(enableGrid)) {
    return <Grid {...GridProps}>{result}</Grid>;
  } else {
    return result;
  }
};

export default MyTextField;
